/**
 * Bonsait API v2 - Cleaner, more maintainable structure
 * 
 * This is a refactored version that separates concerns into dedicated classes:
 * - BonsaitAnalytics: Site usage statistics and event tracking
 * - BonsaitForms: Form spam filtering and validation
 * - BonsaitUtils: General utility functions
 * 
 * @example
 * // Analytics
 * import { Analytics } from './Bonsait-v2';
 * Analytics.setup(); // Initialize and track pageview
 * Analytics.trackEvent('button_click', { button: 'signup' });
 * 
 * @example
 * // Forms
 * import { Forms } from './Bonsait-v2';
 * Forms.setup(); // Initialize spam protection
 * const hpi = Forms.getHumanScore();
 * 
 * @example
 * // Utilities
 * import { Utils } from './Bonsait-v2';
 * const isMobile = Utils.isMobile();
 * Utils.setCookie('prefs', 'dark', 365);
 */

import { BonsaitAnalytics } from './BonsaitAnalytics';
import { BonsaitForms } from './BonsaitForms';
import { BonsaitUtils } from './BonsaitUtils';

// Export with shorter aliases
export { 
    BonsaitAnalytics as Analytics,
    BonsaitForms as Forms,
    BonsaitUtils as Utils
};

// Export individual classes
export { BonsaitAnalytics, BonsaitForms, BonsaitUtils };

// ===== Backward Compatibility Layer =====
// Keep the old API working for existing code

/**
 * Legacy collect() function for backward compatibility
 * @deprecated Use BonsaitAnalytics.setup() and trackEvent() instead
 */
export const collect = (type, arg2, arg3) => {
    if (!type) return false;
    
    try {
        if (type === 'pageview') {
            BonsaitAnalytics.trackPageview(arg2);
        } else if (type === 'event') {
            BonsaitAnalytics.trackEvent(arg2, null, arg3);
        } else if (type === 'setup') {
            BonsaitAnalytics.setup();
        }
        return true;
    } catch (error) {
        console.error('Bonsait.collect error:', error);
        return false;
    }
};

/**
 * Legacy collectEvent() function for backward compatibility
 * @deprecated Use BonsaitAnalytics.trackEvent() instead
 */
export const collectEvent = (name, sitename) => {
    if (!name) return false;
    return collect('event', name, sitename);
};

/**
 * Legacy forms() function for backward compatibility
 * @deprecated Use BonsaitForms methods instead
 */
export const forms = (type, arg2) => {
    if (!type) return false;
    
    try {
        if (type === 'setup') {
            if (arg2) {
                BonsaitForms.setupForm(arg2);
            } else {
                BonsaitForms.setup();
            }
        } else if (type === 'hpi') {
            return BonsaitForms.getHumanScore();
        }
        return true;
    } catch (error) {
        console.error('Bonsait.forms error:', error);
        return false;
    }
};

// Legacy utility exports
export const getBrowser = BonsaitUtils.getBrowser;
export const isMobile = BonsaitUtils.isMobile;
export const getDevice = BonsaitUtils.getDevice;
export const setCookie = BonsaitUtils.setCookie;
export const getCookie = BonsaitUtils.getCookie;
export const getNumberValue = BonsaitUtils.getNumberValue;
export const getParameterByName = BonsaitUtils.getParameterByName;

/**
 * Default export for convenience
 * @example
 * import Bonsait from './Bonsait-v2';
 * Bonsait.Analytics.setup();
 * Bonsait.Forms.setup();
 */
const Bonsait = {
    Analytics: BonsaitAnalytics,
    Forms: BonsaitForms,
    Utils: BonsaitUtils,
    
    // Legacy compatibility
    collect,
    collectEvent,
    forms,
    getBrowser,
    isMobile,
    getDevice,
    setCookie,
    getCookie,
    getNumberValue,
    getParameterByName
};

export default Bonsait;

// ===== Auto-initialization for browser usage =====
// Automatically setup analytics and forms when script loads

if (typeof window !== 'undefined') {
    // Make Bonsait globally available
    window.Bonsait = Bonsait;
    
    // Auto-initialize on page load
    const autoInit = () => {
        try {
            // Setup analytics (tracks pageview automatically)
            BonsaitAnalytics.setup();
            
            // Setup form spam protection
            BonsaitForms.setup();
            
            // Log initialization success
            // console.log('✓ Bonsait initialized (Analytics + Forms)');

        } catch (error) {
            console.error('Bonsait initialization error:', error);
        }
    };
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', autoInit);
    } else {
        // DOM already loaded, initialize immediately
        autoInit();
    }
}
